import type { Icon } from '@phosphor-icons/react';
import {
  BellIcon,
  ChatCenteredDotsIcon,
  ChatCircleIcon,
  EnvelopeSimpleIcon,
  FileTextIcon,
  PaperclipIcon,
  ReceiptIcon,
  TruckIcon,
} from '@phosphor-icons/react';

import { Notification } from './types';

export type NotificationTone = 'negative' | 'warning' | 'info' | 'neutral';

export interface NotificationAppearance {
  /** App section the notification came from, shown in the meta line. */
  category: string;
  icon: Icon;
  tone: NotificationTone;
}

/** Tile tint + icon colour per tone. */
export const TONE_CLASS_NAME: Record<NotificationTone, string> = {
  negative: 'bg-negative-subtle text-negative-text',
  warning: 'bg-warning-subtle text-warning-text',
  info: 'bg-info-subtle text-info-text',
  neutral: 'bg-neutral-subtle text-fg-secondary',
};

/**
 * Notifications carry no type column — the content and the link are all we
 * have — so the icon and tone are derived from the wording used by the
 * `createNotification` call sites. First match wins.
 */
const CONTENT_RULES: { match: RegExp; category?: string; icon: Icon; tone: NotificationTone }[] = [
  { match: /doorrijden niet mogelijk/i, category: 'Trailers', icon: TruckIcon, tone: 'negative' },
  { match: /^trailer\b/i, category: 'Trailers', icon: TruckIcon, tone: 'warning' },
  { match: /klacht/i, category: 'Klachten', icon: ChatCenteredDotsIcon, tone: 'warning' },
  { match: /probleem met e-?mail|bounced?/i, category: 'E-mail', icon: EnvelopeSimpleIcon, tone: 'negative' },
  { match: /offerte/i, category: 'Offertes', icon: FileTextIcon, tone: 'info' },
  { match: /nieuw bericht/i, icon: ChatCircleIcon, tone: 'info' },
  { match: /document/i, icon: PaperclipIcon, tone: 'neutral' },
  { match: /factuur|credit ?nota|self ?billing/i, category: 'Financieel', icon: ReceiptIcon, tone: 'info' },
];

/** Something went wrong — always shown in the negative tone. */
const FAILURE = /\bmislukt\b|\berror\b|\bbounced?\b|\bprobleem\b|\bkon\b[\s\S]*\bniet\b|\bniet mogelijk\b/i;

/** Fallback category, taken from the section the link points at. */
const LINK_CATEGORIES: [RegExp, string][] = [
  [/^\/internal\/trailers/, 'Trailers'],
  [/^\/internal\/orders/, 'Orders'],
  [/^\/internal\/finance\/quotations?/, 'Offertes'],
  [/^\/internal\/finance/, 'Financieel'],
  [/^\/internal\/suppliers/, 'Vervoerders'],
];

function getLinkCategory(link: string | null): string {
  if (!link) return 'Systeem';
  return LINK_CATEGORIES.find(([match]) => match.test(link))?.[1] ?? 'Systeem';
}

export function getNotificationAppearance(notification: Notification): NotificationAppearance {
  const { content, link } = notification;
  const rule = CONTENT_RULES.find((candidate) => candidate.match.test(content));

  return {
    category: rule?.category ?? getLinkCategory(link),
    icon: rule?.icon ?? BellIcon,
    tone: FAILURE.test(content) ? 'negative' : (rule?.tone ?? 'neutral'),
  };
}
